import React, { useState, useRef, useEffect } from 'react';
import { login } from '../utils/api';
import useBreakpoint from '../hooks/useBreakpoint';

const FALLBACK_WAIT_MIN = 15; // the server's lockout window, used if Retry-After is not readable

function rateLimitMessage(err) {
  const sec = Number(err.response?.headers?.['retry-after']);
  const min = Number.isFinite(sec) && sec > 0 ? Math.ceil(sec / 60) : FALLBACK_WAIT_MIN;
  return `Too many attempts. Try again in ${min} ${min === 1 ? 'minute' : 'minutes'}.`;
}

const fieldLabel = {
  display: 'block',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-muted)',
  marginBottom: 6,
};

const inputStyle = (hasError) => ({
  width: '100%',
  height: 48,
  padding: '0 16px',
  background: 'var(--bg-card)',
  border: `1px solid ${hasError ? 'rgba(248,113,113,0.5)' : 'var(--border-med)'}`,
  borderRadius: 10,
  color: 'var(--text)',
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 16,
  outline: 'none',
});

export default function SignIn({ onSignedIn, reason }) {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [show,     setShow]     = useState(false);
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const emailRef    = useRef();
  const passwordRef = useRef();
  const breakpoint  = useBreakpoint();
  const startedOnDesktop = useRef(breakpoint === 'desktop');

  // Desktop only: on phones the keyboard would jump up before the person has seen the screen.
  useEffect(() => { if (startedOnDesktop.current) emailRef.current?.focus(); }, []);

  const canSubmit = email.trim() !== '' && password !== '' && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    try {
      const data = await login(email.trim(), password);
      onSignedIn(data.user);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401)      setError('Invalid email or password');
      else if (status === 429) setError(rateLimitMessage(err));
      else                     setError('Could not sign in. Check your connection and try again.');
      setPassword('');
      setLoading(false);
      passwordRef.current?.focus();
    }
  };

  return (
    <div className="signin-root" style={{
      minHeight: '100vh',
      background: 'var(--bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      padding: 24,
    }}>
      <div className="signin-card" style={{
        background: '#FFFFFF',
        border: '1px solid var(--border)',
        borderRadius: 16,
        padding: '40px 32px',
        width: '100%',
        maxWidth: 380,
        textAlign: 'center',
        boxShadow: 'var(--shadow)',
      }}>
        <img src="/logo-horizontal.png" alt="Syncrea Outreach Tracker" style={{ height: 72, maxWidth: '100%', marginBottom: 20, objectFit: 'contain' }} />

        <h1 style={{ margin: '0 0 4px', fontFamily: 'Sora, sans-serif', fontSize: 20, fontWeight: 600, color: 'var(--text)' }}>
          Sign in
        </h1>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 24, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Enter your details to continue
        </div>

        {reason === 'expired' && (
          <div role="status" style={{
            background: 'var(--bg)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            color: 'var(--text-muted)',
            fontSize: 13,
            padding: '10px 12px',
            marginBottom: 16,
          }}>
            Your session ended. Please sign in again.
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div style={{ marginBottom: 16 }}>
            <label htmlFor="signin-email" style={fieldLabel}>Email</label>
            <input
              id="signin-email"
              ref={emailRef}
              type="email"
              name="email"
              autoComplete="username"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              maxLength={254}
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
              style={inputStyle(Boolean(error))}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label htmlFor="signin-password" style={fieldLabel}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                id="signin-password"
                ref={passwordRef}
                type={show ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                maxLength={200}
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                style={{ ...inputStyle(Boolean(error)), paddingRight: 72 }}
              />
              <button
                type="button"
                className="signin-toggle"
                aria-pressed={show}
                aria-label="Show password"
                onClick={() => setShow(s => !s)}
                style={{
                  position: 'absolute', top: 2, right: 2,
                  minWidth: 44, height: 44, padding: '0 10px',
                  background: 'transparent', border: 'none', borderRadius: 8,
                  color: 'var(--primary)',
                  fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {show ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          {error && (
            <div role="alert" style={{
              background: 'rgba(248,113,113,0.1)',
              border: '1px solid rgba(248,113,113,0.3)',
              borderRadius: 8,
              color: '#F87171',
              fontSize: 13,
              padding: '8px 12px',
              marginBottom: 16,
              textAlign: 'left',
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              width: '100%',
              minHeight: 48,
              padding: '14px',
              background: canSubmit
                ? 'linear-gradient(135deg, var(--primary), var(--primary-dark))'
                : '#E5E7EB',
              border: 'none',
              borderRadius: 10,
              color: canSubmit ? '#fff' : 'var(--text-dim)',
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: 600,
              fontSize: 15,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              transition: 'all 0.15s',
            }}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div style={{ marginTop: 20, fontSize: 12, color: 'var(--text-dim)' }}>
          Forgot your password? Ask Bruce or Susan to reset it.
        </div>
      </div>

      <div style={{ marginTop: 20, fontSize: 11, color: 'var(--text-dim)' }}>
        SyncHub 360 Inc. · Syncrea
      </div>
    </div>
  );
}
